import { Controller } from "@hotwired/stimulus"
import { show_error, show_success, showBlock } from "lib/helpers"

// Manages the assistant edit shell (shared across edit_simple, edit_advanced,
// edit_chat_workflows). Handles: AJAX form submission, copy-to-clipboard,
// delete confirmation, text counters, tab scrolling, Elastic RUM, and
// the WorkflowManager bridge for edit_chat_workflows.
//
// Usage:
//   .wrap_bot{ data: { controller: "assistants--edit-shell",
//     assistants__edit_shell_error_block_value: "#chatbot-errors",
//     assistants__edit_shell_telemetry_url_value: "...",
//     assistants__edit_shell_telemetry_env_value: "..." } }
export default class extends Controller {
  static values = {
    errorBlock: { type: String, default: "#chatbot-errors" },
    confirmDeleteErrorBlock: { type: String, default: "#confirm-bot-deletion-errors" },
    telemetryUrl: String,
    telemetryEnv: String,
    assistantsPath: String,
  }

  connect() {
    this.initElasticRum()
    this.initBotFormSubmitHandler()
    this.initDeleteButtonHandler()
    this.initCopyIdButton()
    this.initSetFileNameHandler()
    this.initSystemMessageCounter()
    this.initTextInputCounter()
    this.initResetSystemMessagesHandlers()
    this.scrollTabToView()
    this.initWorkflowManager()
  }

  disconnect() {
    if (window.WorkflowManager) {
      window.WorkflowManager.destroy()
    }
  }

  initElasticRum() {
    if (!window.elasticApm) return
    if (elasticApm._initialized) {
      window.elasticApm.setInitialPageLoadName("Smart Chat Assistant Edit Page")
    } else if (this.telemetryUrlValue && this.telemetryEnvValue) {
      window.elasticApm.init({
        serviceName: this.telemetryEnvValue,
        serverUrl: this.telemetryUrlValue,
        disableInstrumentations: ['page-load', 'history', 'eventtarget', 'click', 'xmlhttprequest', 'fetch', 'error']
      })
    }
  }

  initBotFormSubmitHandler() {
    const form = $('#edit-bot-form')
    if (form.length === 0) return
    const errorBlock = this.errorBlockValue
    form.ajaxForm({
      dataType: 'json',
      success(response) {
        if (response.success) {
          show_success(response.message)
        } else {
          const error = $(errorBlock)
          if (error.length === 1) error.after(error.clone())
          showBlock(errorBlock, response.message)
        }
        $.scrollTo('body', 'slow')
        form.find("input[type='submit']").enable(true)
      },
      error() {
        $.scrollTo('body', 'slow')
        showBlock(errorBlock, "An error occurred during request")
      }
    })
  }

  initDeleteButtonHandler() {
    const self = this
    $('#delete-bot-with-confirmation-btn').on('click.editShell', function (e) {
      e.preventDefault()
      $("#confirmation_delete_bot").modal("show")
      self.initDeleteBotConfirmation()
    })
  }

  initDeleteBotConfirmation() {
    const errorBlock = this.confirmDeleteErrorBlockValue
    const form = $('#confirm_delete_bot_form')
    form.ajaxForm({
      dataType: 'json',
      method: "DELETE",
      success(response) {
        if (response.success) {
          $("#confirmation_delete_bot").modal("hide")
          location.href = response.path
        } else {
          showBlock(errorBlock, response.message)
        }
        form.find("input[type='submit']").enable(true)
      },
      error() {
        $("#confirmation_delete_bot").modal("hide")
        show_error("Unexpected error occurred during request")
      }
    })
  }

  initCopyIdButton() {
    this.element.querySelectorAll('.bg-copy').forEach(el => {
      el.addEventListener('click', function (e) {
        e.preventDefault()
        const textToCopy = $(this).data('copy-value')
        if (!textToCopy) return
        const temp = document.createElement('textarea')
        temp.value = textToCopy
        document.body.appendChild(temp)
        temp.select()
        if (document.execCommand('copy')) {
          $(this).addClass('copy-success')
          const self = $(this)
          setTimeout(() => self.removeClass('copy-success'), 2000)
        }
        document.body.removeChild(temp)
      })
    })
  }

  initSetFileNameHandler() {
    $(".inputFile").on("change.editShell paste.editShell keyup.editShell", function () {
      const errorBlock = '#chatbot-errors'
      $('.file-name').val($(this).val().replace(/.*(\/|\\)/, ''))
      const files = this.files
      if (files && files[0]) {
        if (files[0].size > 26214400) {
          showBlock(errorBlock, "File is too large (current: " + files[0].size + " Bytes, max: 25MB (26214400 Bytes).")
          $(".inputFile").val("")
          $.scrollTo('body', 'slow')
          return
        }
        if (files[0].size === 0) {
          showBlock(errorBlock, "The file is empty. Please choose a valid file with content.")
          $(".inputFile").val("")
          $.scrollTo('body', 'slow')
        }
      }
    })
  }

  initSystemMessageCounter() {
    $(this.element).on('change.editShell paste.editShell keyup.editShell', 'textarea', function () {
      $(this).parent().find('.textaria-counter span').html($(this).val().length)
    })
  }

  initTextInputCounter() {
    $(this.element).on('change.editShell paste.editShell keyup.editShell', 'input[type="text"]', function () {
      $(this).parent().find('.textaria-counter span').html($(this).val().length)
    })
  }

  initResetSystemMessagesHandlers() {
    const errorBlock = this.errorBlockValue
    $(this.element).on('click.editShell', '.chatbot-reset-system-message', function (e) {
      e.preventDefault()
      const textareaName = $(this).data('reset-param')
      $.ajax({
        url: this.href,
        method: "PATCH",
        dataType: "json",
        contentType: 'application/json',
        success(response) {
          if (response.success) {
            show_success("System messages have been reset successfully.")
            $('#chat_assistant_' + textareaName).val(response.system_message)
          } else {
            showBlock(errorBlock, response.message)
          }
        },
        error() {
          showBlock(errorBlock, "An error occurred while resetting system messages.")
        }
      })
    })
  }

  scrollTabToView() {
    const activeTab = this.element.querySelector('.assistant-tab-button.active')
    if (activeTab) {
      activeTab.scrollIntoView({ behavior: 'auto', inline: 'center', block: 'nearest' })
    }
  }

  initWorkflowManager() {
    if (this.element.querySelector('.chat-workflows-container') && window.WorkflowManager) {
      window.WorkflowManager.init()
    }
  }
}
